import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { A11yProvider } from "./a11y/A11yContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import LoginPage from "./pages/LoginPage";
import LoginCompletePage from "./pages/LoginCompletePage";
import ForgotPage from "./pages/ForgotPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import LogPage from "./pages/LogPage";
import JobsPage from "./pages/JobsPage";
import ProfilePage from "./pages/ProfilePage";
import StatsPage from "./pages/StatsPage";
import AccessibilityStatementPage from "./pages/AccessibilityStatementPage";
import QuotationSignPage, { OldSigningLinkPage } from "./pages/QuotationSignPage";
import PdfViewPage from "./pages/PdfViewPage";

export default function App() {
  return (
    // Outside AuthProvider: the accessibility settings have to apply on the
    // sign-in screen too, which is where someone who needs them will first
    // reach for them.
    <A11yProvider>
    <AuthProvider>
      <BrowserRouter>
        {/* Inside the router, so "Back to projects" and a reload both work, and
            so a crash in one page cannot blank the whole application — which is
            what happened when Settings threw during render. */}
        <ErrorBoundary>
        <Routes>
          <Route path="/login"         element={<LoginPage />} />
          {/* Where the Keycloak callback hands over the portal token, in the
              fragment. Not /auth/complete: nginx proxies /auth/ to the backend. */}
          <Route path="/login/complete" element={<LoginCompletePage />} />
          <Route path="/forgot"          element={<ForgotPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          <Route path="/accessibility"   element={<AccessibilityStatementPage />} />
          {/* Public, and no longer a credential: the signing links mailed
              before 2026-09-24 land here and are met with a sentence and a
              Sign in button. The page sends nothing, least of all the token. */}
          <Route path="/quotation"       element={<OldSigningLinkPage />} />
          <Route path="/"              element={<Navigate to="/projects" replace />} />
          <Route path="/projects"      element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
          <Route path="/projects/:id"  element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
          {/* Where the quotation mail links to. Behind the login, which returns
              here through `?next=`; the server checks that the signed-in
              account is a signatory of this quotation. */}
          <Route path="/projects/:pid/quotations/:qid/sign"
                 element={<ProtectedRoute><QuotationSignPage /></ProtectedRoute>} />
          {/* Every role gets Jobs — a plain user's own upload is exactly the
              thing that outlives their session and needs picking back up. */}
          <Route path="/jobs"          element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
          {/* Where api.openFile sends a PDF (2026-10-06): a portal page with
              the name, Back to portal and Download. Takes only `?path=/api/…`. */}
          <Route path="/view"          element={<ProtectedRoute><PdfViewPage /></ProtectedRoute>} />
          <Route path="/profile"       element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/stats"         element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
          <Route path="/admin/users"   element={<ProtectedRoute operatorOrAdmin><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/log"     element={<ProtectedRoute operatorOrAdmin><LogPage /></ProtectedRoute>} />
          <Route path="*"              element={<Navigate to="/projects" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </A11yProvider>
  );
}
